import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 5500;

/**
 * Ephemeral success / warning / error lines for the CRE floating mirror (`mirror-state`).
 * Use from patient sidebar, future RTE flows, etc.: call `notify*` with the same user-facing
 * string you would toast; merge `payloadFields` into `mirrorPayloadExtension` passed to
 * `useCreDictationMirror`.
 */
export function useCreMirrorFeedback() {
  const [token, setToken] = useState(0);
  const [level, setLevel] = useState("");
  const [message, setMessage] = useState("");
  const clearTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    },
    [],
  );

  const clear = useCallback(() => {
    if (clearTimerRef.current) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    setLevel("");
    setMessage("");
  }, []);

  const notify = useCallback((nextLevel, msg, durationMs = DEFAULT_DURATION_MS) => {
    if (typeof msg !== "string" || !msg.trim()) return;
    if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    setToken((x) => x + 1);
    setLevel(nextLevel);
    setMessage(msg.trim());
    clearTimerRef.current = window.setTimeout(() => {
      clearTimerRef.current = null;
      setLevel("");
      setMessage("");
    }, durationMs);
  }, []);

  const notifySuccess = useCallback((msg) => notify("success", msg), [notify]);
  const notifyWarning = useCallback((msg) => notify("warning", msg), [notify]);
  const notifyError = useCallback((msg) => notify("error", msg), [notify]);

  const payloadFields = useMemo(
    () => ({
      mirrorFeedbackToken: token,
      mirrorFeedbackLevel: level,
      mirrorFeedbackMessage: message,
    }),
    [token, level, message],
  );

  return {
    payloadFields,
    notifySuccess,
    notifyWarning,
    notifyError,
    clear,
  };
}
